/*
 * File: MSTIsNumber.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Checks the property type of a Mobx State Tree prop to see if it's
 * a number.  If it is, the val needs to be converted to a number
 *
 * The val would be coming back from an html select input, which by default, converts
 * the value to a string.  If the prop is a types.number in the model, mst will
 * throw an error
 *
 * We're testing the key prop type by seeing if the word "number" is included in the type
 * string.  MST will give either "number" or "(number || null)" for numeric types
 */
import { getType, IAnyModelType, IStateTreeNode } from "mobx-state-tree";

/**
 * A conditional type which normally outputs the input type T, but when T is a number outputs T | string to allow passing a numeric string or a number directly.
 * Ensure you update `mstIsNumber` if you make changes to this type.
 */
export type ValueOrNumeric<T> = T extends number ? T | string : T;

/**
 * @param {Object} model Mobx State Tree 'self' object
 * @param {String} key Name of the Mobx State Tree prop
 * @param {String} val Value coming back from an html select input
 * @return {String|Number}
 */
const mstIsNumber = <
  T extends IStateTreeNode<IAnyModelType>,
  K extends keyof T
>(
  model: T,
  key: K,
  val: ValueOrNumeric<T[K]>
): T[K] => {
  const type = getType(model) as IAnyModelType;
  const properties = type.properties[key];
  if (properties && properties.name.includes("number")) {
    // Assuming the above guard works correctly, this should only ever run when T[K] is already number so the cast should be safe
    return (Number(val) as unknown) as T[K];
  } else {
    // `ValueOrNumeric` only allows passing a non-`T[K]` value when `T[K]` is `number` which should be handled by the above check.
    return val as T[K];
  }
};

export default mstIsNumber;
